/* Public club/group registration — schools submit a group for admin approval.
   Writes to Firestore as `pending`. Shared as a standalone link (not in main nav). */
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "../../lib/router-compat.jsx";
import PublicShell from "../../components/shell/PublicShell.jsx";
import { Button, Field, Input, Card, Icon, showToast } from "../../ui/index.js";
import { addSchoolGroup } from "../../data/mutations.js";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const emptyLeader = () => ({ name: "", email: "", phone: "", position: "", occupation: "" });

export default function RegisterClubPage() {
  const [form, setForm] = useState({ schoolName: "", memberCount: "", meetingDays: [] });
  const [leaders, setLeaders] = useState([emptyLeader()]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleDay = (day) =>
    setForm((f) => ({
      ...f,
      meetingDays: f.meetingDays.includes(day)
        ? f.meetingDays.filter((d) => d !== day)
        : [...f.meetingDays, day],
    }));

  const updateLeader = (i, k) => (e) => {
    const v = e.target.value;
    setLeaders((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  };
  const addLeader = () => setLeaders((ls) => [...ls, emptyLeader()]);
  const removeLeader = (i) => setLeaders((ls) => ls.filter((_, idx) => idx !== i));

  const validate = () => {
    const e = {};
    if (!form.schoolName.trim()) e.schoolName = "Please enter the school name.";
    const count = Number(form.memberCount);
    if (form.memberCount === "" || Number.isNaN(count) || count < 0)
      e.memberCount = "Enter the number of members (0 or more).";
    if (form.meetingDays.length === 0) e.meetingDays = "Pick at least one meeting day.";

    const leaderErrors = leaders.map((l) => {
      const le = {};
      if (!l.name.trim()) le.name = "Required";
      if (!l.email.trim()) le.email = "Required";
      else if (!/^\S+@\S+\.\S+$/.test(l.email)) le.email = "Invalid email";
      if (!l.phone.trim()) le.phone = "Required";
      if (!l.position.trim()) le.position = "Required";
      return le;
    });
    if (leaderErrors.some((le) => Object.keys(le).length)) e.leaders = leaderErrors;
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length) {
      showToast({ intent: "warning", title: "Please fix the highlighted fields." });
      return;
    }
    setSubmitting(true);
    try {
      await addSchoolGroup({
        schoolName: form.schoolName.trim(),
        memberCount: Math.round(Number(form.memberCount)),
        meetingDays: form.meetingDays,
        leaders: leaders.map((l) => ({
          name: l.name.trim(),
          email: l.email.trim(),
          phone: l.phone.trim(),
          position: l.position.trim(),
          occupation: l.occupation.trim(),
        })),
      });
      setSuccess({ schoolName: form.schoolName.trim() });
    } catch (err) {
      showToast({ intent: "danger", title: "Couldn't submit your registration", body: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicShell>
      <Helmet><title>Register a club — Purple Angels Kids Foundation</title></Helmet>
      <section className="section">
        <div className="container" style={{ maxWidth: 760 }}>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <div className="icon-circle" style={{ margin: "0 auto 16px" }}><Icon.School size={24} /></div>
            <h1 className="t-3xl" style={{ margin: 0 }}>Register your school club</h1>
            <p className="t-base muted" style={{ marginTop: 8 }}>
              Tell us about your group. Our team will review your submission and confirm once it's officially registered.
            </p>
          </div>

          <Card variant="elevated" style={{ padding: 32 }}>
            {success ? (
              <div style={{ textAlign: "center", padding: "16px 0" }}>
                <div style={{ width: 64, height: 64, borderRadius: "50%", background: "var(--success-bg)", color: "var(--success)", display: "grid", placeItems: "center", margin: "0 auto 16px" }}>
                  <Icon.Check size={32} />
                </div>
                <h2 className="t-2xl" style={{ marginTop: 0 }}>Submission received!</h2>
                <p className="t-base muted">
                  Thanks for registering <strong>{success.schoolName}</strong>. Our team will review the details and be in
                  touch with your leadership contacts. Once approved, your group will be officially registered.
                </p>
                <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 16, flexWrap: "wrap" }}>
                  <Button
                    variant="ghost"
                    onClick={() => { setSuccess(null); setForm({ schoolName: "", memberCount: "", meetingDays: [] }); setLeaders([emptyLeader()]); setErrors({}); }}
                  >
                    Register another club
                  </Button>
                  <Link to="/" className="btn btn--secondary">Back to home</Link>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} noValidate>
                <div className="stack-lg">
                  <Field label="School name" required error={errors.schoolName} htmlFor="rc-school">
                    <Input id="rc-school" value={form.schoolName} onChange={update("schoolName")} error={!!errors.schoolName} placeholder="e.g. Accra Girls Senior High School" />
                  </Field>

                  <Field label="Number of members" required error={errors.memberCount} helper="Total count of members in the group." htmlFor="rc-count">
                    <Input id="rc-count" type="number" min="0" value={form.memberCount} onChange={update("memberCount")} error={!!errors.memberCount} placeholder="e.g. 24" />
                  </Field>

                  <Field label="Meeting days" required error={errors.meetingDays} helper="Select all the days the group meets.">
                    <div className="chips" style={{ marginTop: 4 }}>
                      {DAYS.map((day) => (
                        <button
                          type="button"
                          key={day}
                          className={`chip ${form.meetingDays.includes(day) ? "active" : ""}`}
                          aria-pressed={form.meetingDays.includes(day)}
                          onClick={() => toggleDay(day)}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </Field>

                  <div>
                    <div className="row row--between" style={{ alignItems: "center", marginBottom: 4 }}>
                      <label className="field__label" style={{ margin: 0 }}>Leadership contacts<span className="req">*</span></label>
                    </div>
                    <p className="field__helper" style={{ marginTop: 0, marginBottom: 12 }}>Add one or more people who lead this group.</p>

                    <div className="stack-md" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                      {leaders.map((leader, i) => {
                        const le = (errors.leaders && errors.leaders[i]) || {};
                        return (
                          <div key={i} style={{ border: "1px solid var(--border)", borderRadius: "var(--radius-md)", padding: 16, background: "var(--bg-warm)" }}>
                            <div className="row row--between" style={{ alignItems: "center", marginBottom: 12 }}>
                              <div className="t-sm" style={{ fontWeight: 600 }}>Leader {i + 1}</div>
                              {leaders.length > 1 && (
                                <Button type="button" variant="ghost" size="sm" onClick={() => removeLeader(i)} style={{ color: "var(--danger)" }}>
                                  <Icon.Trash size={14} />Remove
                                </Button>
                              )}
                            </div>
                            <div className="leader-grid">
                              <Field label="Name" required error={le.name} htmlFor={`ld-name-${i}`}>
                                <Input id={`ld-name-${i}`} value={leader.name} onChange={updateLeader(i, "name")} error={!!le.name} placeholder="Full name" />
                              </Field>
                              <Field label="Position" required error={le.position} htmlFor={`ld-pos-${i}`}>
                                <Input id={`ld-pos-${i}`} value={leader.position} onChange={updateLeader(i, "position")} error={!!le.position} placeholder="e.g. Patron, President" />
                              </Field>
                              <Field label="Email" required error={le.email} htmlFor={`ld-email-${i}`}>
                                <Input id={`ld-email-${i}`} type="email" value={leader.email} onChange={updateLeader(i, "email")} error={!!le.email} placeholder="name@example.com" />
                              </Field>
                              <Field label="Phone" required error={le.phone} htmlFor={`ld-phone-${i}`}>
                                <Input id={`ld-phone-${i}`} value={leader.phone} onChange={updateLeader(i, "phone")} error={!!le.phone} placeholder="+233…" />
                              </Field>
                              <Field label="Occupation" htmlFor={`ld-occ-${i}`} className="field--span-2">
                                <Input id={`ld-occ-${i}`} value={leader.occupation} onChange={updateLeader(i, "occupation")} placeholder="e.g. Teacher (optional)" />
                              </Field>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <Button type="button" variant="secondary" size="sm" onClick={addLeader} style={{ marginTop: 12 }}>
                      <Icon.Plus size={14} />Add another leader
                    </Button>
                  </div>

                  <Button type="submit" variant="primary" size="lg" loading={submitting} className="btn--full">
                    {submitting ? "Submitting…" : "Submit registration"}
                  </Button>
                  <p className="t-xs muted center" style={{ margin: 0 }}>
                    By submitting, you agree to our <Link to="/privacy">Privacy Notice</Link>.
                  </p>
                </div>
              </form>
            )}
          </Card>
        </div>
      </section>
    </PublicShell>
  );
}
